/* File : src/pages/GuestPages/TicketPage.js */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from '../../components/common/QRCode';
import Button from '../../components/common/Button';
import { publicApi } from '../../services/api';
import { useEvent } from '../../contexts/EventContext';
import '../../components/guest/Ticket.css';

/**
 * Page d'affichage du ticket après inscription ou récupération
 * 
 * VERSION ULTRA-SIMPLIFIÉE pour debug
 */
const TicketPage = () => {
  const { phone: phoneParam } = useParams();
  const navigate = useNavigate();
  const { eventDetails, eventId, loading: eventLoading, changeEvent } = useEvent();
  const [userData, setUserData] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger les données de l'utilisateur et du ticket
  useEffect(() => {
    const fetchUserData = async () => {
      if (!phoneParam) {
        navigate('/');
        return;
      }
      
      setLoading(true);
      
      try {
        console.log('Fetching user data for phone:', phoneParam);
        
        // Obtenir les informations de l'utilisateur par téléphone
        const response = await publicApi.getUserByPhone(phoneParam);
        
        console.log('API Response:', response);
        console.log('Response.data:', response.data);
        
        // ✅ STRUCTURE AXIOS : response.data contient la réponse du backend
        const backendResponse = response.data;
        
        console.log('Backend Response:', backendResponse);
        console.log('Backend Response.success:', backendResponse.success);
        console.log('Backend Response.data:', backendResponse.data);
        
        // ✅ VÉRIFICATION : Le backend retourne {success: true, data: {...}}
        if (backendResponse && backendResponse.success && backendResponse.data) {
          const apiData = backendResponse.data;
          
          console.log('User data from backend:', apiData);
          
          // ✅ Vérifier que les données essentielles existent
          if (!apiData.id) {
            throw new Error('Données utilisateur incomplètes - ID manquant');
          }
          
          // ✅ Vérifier que le ticket existe
          if (!apiData.ticket_id) {
            throw new Error('Ticket introuvable - ticket_id manquant');
          }
          
          if (!apiData.qr_code) {
            throw new Error('Ticket invalide - qr_code manquant');
          }
          
          // Formater les données utilisateur
          const userData = {
            id: apiData.id,
            firstName: apiData.first_name || '',
            lastName: apiData.last_name || '',
            phone: apiData.phone || '',
            gender: apiData.gender || null,
            district: apiData.district || null
          };
          
          // Formater les données du ticket
          const ticketData = {
            ticket_id: apiData.ticket_id,
            ticket_type: apiData.ticket_type_confirmed || apiData.ticket_type || 'classic',
            qrData: apiData.qr_code,
            ticketCode: apiData.ticket_code || apiData.qr_code,
            event_id: apiData.event_id || eventId || 1,
            purchase_amount: parseFloat(apiData.purchase_amount || 0),
            voucher_amount: parseFloat(apiData.voucher_amount || 0),
            is_used: apiData.is_used || false,
            is_validated: apiData.is_validated || false
          };
          
          console.log('Formatted userData:', userData);
          console.log('Formatted ticketData:', ticketData);
          
          setUserData(userData);
          setTicketData(ticketData);
          
          // Synchroniser l'event_id si nécessaire
          if (ticketData.event_id && ticketData.event_id !== eventId) {
            console.log('Synchronizing event context with ticket event ID:', ticketData.event_id);
            localStorage.setItem('currentEventId', ticketData.event_id.toString());
            if (changeEvent) {
              changeEvent(ticketData.event_id.toString());
            }
          }
          
        } else {
          console.error('Invalid backend response structure:', backendResponse);
          throw new Error('Structure de réponse invalide');
        }
        
      } catch (err) {
        console.error('Error fetching user data:', err);
        
        // Messages d'erreur plus précis
        let errorMessage = 'Une erreur est survenue lors du chargement du ticket.';
        
        if (err.response) {
          // Erreur de réponse du serveur
          if (err.response.status === 404) {
            errorMessage = 'Aucun ticket trouvé pour ce numéro de téléphone.';
          } else if (err.response.data && err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        
        // Redirection après 3 secondes
        setTimeout(() => {
          navigate('/retrieve-ticket');
        }, 3000);
        
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [phoneParam, navigate, eventId, changeEvent]);
  
  const handleBack = () => {
    navigate('/event');
  };
  
  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };
  
  // Formater l'heure pour l'affichage
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };
  
  // Formater le numéro de téléphone pour l'affichage
  const formatPhone = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Exemple: 650123456 => 650 12 34 56
    return phoneNumber.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };
  
  // Obtenir les informations du bon d'achat selon le type de ticket
  const getVoucherInfo = (ticketType, voucherAmount) => {
    // ✅ Utiliser le montant réel depuis la BDD
    if (voucherAmount && voucherAmount > 0) {
      const formattedAmount = voucherAmount.toLocaleString('fr-FR') + ' FCFA';
      
      const descriptions = {
        classic: 'Ticket Classique',
        gold: 'Valable dans tous les stands',
        vip: 'Valable sur tous les stands VIP'
      };
      
      return {
        amount: formattedAmount,
        description: descriptions[ticketType] || 'Valable dans tous les stands'
      };
    }
    
    // Fallback si pas de montant en BDD
    const voucherInfo = {
      classic: { amount: 'OFFERT', description: 'Ticket Classique' },
      gold: { amount: '1 500 FCFA', description: 'Valable dans tous les stands' },
      vip: { amount: '50 000 FCFA', description: 'Valable sur tous les stands VIP' }
    };
    
    return voucherInfo[ticketType] || voucherInfo.classic;
  };
  
  const handleShareOnWhatsApp = () => {
    const source = new URLSearchParams(window.location.search).get('source') || 'direct';
    const invitation = new URLSearchParams(window.location.search).get('invitation') || 'guinness';
    const userPhoneNumber = userData?.phone || '';
    const appUrl = `${window.location.origin}/brightnight/?source=whatsapp&invitation=${invitation}&referrer=${userPhoneNumber}`;
    const eventName = eventDetails ? eventDetails.name : 'l\'événement Guinness Bright Night';
    const eventDate = eventDetails ? formatDate(eventDetails.date) : 'le 13 Décembre 2025';
    const shareText = encodeURIComponent(`Je participe à ${eventName} à Yaoundé le ${eventDate} ! Inscrivez-vous aussi via ce lien : ${appUrl}`);
    window.open(`https://wa.me/?text=${shareText}`, '_blank');
  };
  
  const handleShareOnFacebook = () => {
    const invitation = new URLSearchParams(window.location.search).get('invitation') || 'guinness';
    const userPhoneNumber = userData?.phone || '';
    const appUrl = encodeURIComponent(`${window.location.origin}/brightnight/?source=facebook&invitation=${invitation}&referrer=${userPhoneNumber}`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${appUrl}`, '_blank', 'width=600,height=400');
  };
  
  const handleDownloadPrint = () => {
    window.print();
  };
  
  // Afficher un message d'erreur si nécessaire
  if (error) {
    return (
      <div className="ticket-container">
        <div className="ticket-error">
          <div className="ticket-error-icon">❌</div>
          <h2>Désolé</h2>
          <p>{error}</p>
          <p>Vous allez être redirigé...</p>
        </div>
      </div>
    );
  }
  
  // Afficher le chargement pendant la récupération des données
  if (loading || eventLoading || !userData || !ticketData) {
    return (
      <div className="ticket-container">
        <div className="ticket-loading">
          <div className="ticket-spinner"></div>
          <p>Chargement de votre pass...</p>
        </div>
      </div>
    );
  }
  
  // Obtenir le prénom et le nom
  const firstName = userData.firstName || '';
  const lastName = userData.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  // Obtenir le téléphone
  const userPhoneNumber = userData.phone || '';
  
  // Obtenir le type de ticket
  const ticketType = ticketData.ticket_type || 'classic';
  const voucherInfo = getVoucherInfo(ticketType, ticketData.voucher_amount);
  
  return (
    <div className="ticket-container">
      <div className="back-arrow" onClick={handleBack}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      
      <div className="ticket-header">
        <img src="https://guinnessevents.cm/brightnight/assets/images/logo-guinness-bright-nights.png" alt="Guinness Bright Night" className="ticket-logo" />
      </div>
      
      <div className="ticket-content-container">
        <div className="ticket-content">
          <div className="ticket-info-wrapper">
            {/* QR Code et informations sur la même ligne */}
            <div className="ticket-main-section">
              {/* QR Code à gauche */}
              <div className="ticket-qrcode-section">
                <QRCode
                  data={ticketData.qrData}
                  size={175}
                  logo="/assets/images/guinness-icon.png"
                  logoSize={40}
                />
                <div className="ticket-code">{ticketData.ticketCode}</div>
              </div>
              
              {/* Informations à droite */}
              <div className="ticket-info-section">
                <div className="ticket-info-row">
                  <div className="ticket-info-label">Nom complet</div>
                  <div className="ticket-info-value">{fullName}</div>
                </div>
                
                <div className="ticket-info-row">
                  <div className="ticket-info-label">Téléphone</div>
                  <div className="ticket-info-value">{formatPhone(userPhoneNumber)}</div>
                </div>
                
                {eventDetails && (
                  <>
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Lieu</div>
                      <div className="ticket-info-value">{eventDetails.location}</div>
                    </div>
                    
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Date</div>
                      <div className="ticket-info-value">{formatDate(eventDetails.date)}</div>
                    </div>
                    
                    <div className="ticket-info-row">
                      <div className="ticket-info-label">Heure</div>
                      <div className="ticket-info-value">{formatTime(eventDetails.date)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Section bon d'achat */}
            <div className="voucher-section">
              <h3>🎁 Invitation Offerte</h3>
              <p className={`voucher-amount ${voucherInfo.amount === 'OFFERT' ? 'voucher-amount-free' : ''}`}>{voucherInfo.amount}</p>
              <p className="voucher-description">{voucherInfo.description}</p>
            </div>
            
            {/* Encadré contact VIP (uniquement pour les tickets VIP) */}
            {ticketType === 'vip' && (
              <div className="vip-contact-info">
                <h3>📞 Contact VIP</h3>
                <p>Pour toute question ou assistance VIP, contactez-nous:</p>
                <a href="tel:+237XXXXXXXXX" className="vip-phone">
                  +237 6XX XXX XXX
                </a>
                <p className="vip-hours">
                  Disponible: Lundi - Vendredi, 9h - 18h
                </p>
              </div>
            )}
            
            {/* Section partage */}
            <div className="ticket-share-section">
              <h3 className="ticket-sharing-title">PARTAGEZ L'ÉVÉNEMENT</h3>
              
              <Button
                variant="challenge"
                size="md"
                fullWidth
                onClick={() => window.open('https://brightnight237.com/', '_blank')}
                className="challenge-btn"
              >
                Je participe au challenge
              </Button>
              
              <div className="ticket-share-buttons">
                <Button
                  variant="guinness"
                  size="md"
                  fullWidth
                  onClick={handleShareOnWhatsApp}
                >
                  Partagez sur Whatsapp
                </Button>
                
                <Button
                  variant="guinness"
                  size="md"
                  fullWidth
                  onClick={handleShareOnFacebook}
                >
                  Partagez sur Facebook
                </Button>
                
                <Button
                  variant="outline"
                  size="md"
                  fullWidth
                  onClick={handleDownloadPrint}
                >
                  Télécharger / Imprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="ticket-footer">
        <p>
          La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
          L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
        </p>
      </div>
    </div>
  );
};

export default TicketPage;